import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO/SEO';

import { useNavigate } from 'react-router-dom';
import { blogService } from '../../services/blogService';
import { storageService } from '../../services/storageService';
import { sitemapService } from '../../services/sitemapService';
import ContentEditor from '../../components/ContentEditor/ContentEditor';
import './Dashboard.css';

const Dashboard = ({ setIsAuthenticated }) => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        slug: '',
        title: '',
        excerpt: '',
        category: 'SEO',
        author: 'Zia Abdullah',
        date: new Date().toISOString().split('T')[0],
        heroImage: '',
        tags: '',
        content: ''
    });


    useEffect(() => {
        loadBlogs();
    }, []);

    const loadBlogs = async () => {
        try {
            // Fetch blogs from Supabase
            const response = await blogService.getAllPosts();
            
            let supabaseBlogs = [];
            if (response.success && response.data) {
                // Convert Supabase format to dashboard format
                supabaseBlogs = response.data.map(post => ({
                    id: post.id,
                    slug: post.slug,
                    title: post.title,
                    excerpt: post.excerpt,
                    category: post.category,
                    author: post.author,
                    date: post.date,
                    heroImage: post.hero_image,
                    tags: post.tags || [],
                    content: post.content,
                    dateCreated: post.created_at
                }));
            }
            
            // Sort by date (newest first)
            supabaseBlogs.sort((a, b) => new Date(b.date) - new Date(a.date));
            setBlogs(supabaseBlogs);
        } catch (error) {
            console.error('Error loading blogs:', error);
            alert('Error loading blogs from database');
            setBlogs([]);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminAuthenticated');
        localStorage.removeItem('adminLoginTime');
        setIsAuthenticated(false);
        navigate('/admin');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should be less than 5MB');
                return;
            }

            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
                return;
            }

            // Store the file for upload
            setImageFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        try {
            let imageUrl = formData.heroImage;

            // Upload image to Supabase Storage if new image selected
            if (imageFile) {
                const uploadResult = await storageService.uploadImage(imageFile);
                
                if (uploadResult.success) {
                    imageUrl = uploadResult.url;
                } else {
                    alert(`Image upload failed: ${uploadResult.error}`);
                    setUploading(false);
                    return;
                }
            }

            // Process tags - convert comma-separated string to array
            const tagsArray = formData.tags
                ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
                : [];

            const blogData = {
                slug: formData.slug,
                title: formData.title,
                excerpt: formData.excerpt,
                content: formData.content,
                category: formData.category,
                author: formData.author,
                date: formData.date,
                heroImage: imageUrl,
                tags: tagsArray
            };

            let response;
            if (editingBlog) {
                // Update existing blog
                response = await blogService.updatePost(editingBlog.slug, blogData);
            } else {
                // Create new blog
                response = await blogService.createPost(blogData);
            }

            if (response.success) {
                await loadBlogs();
                resetForm();
                
                // Notify about sitemap update
                const sitemapMessage = sitemapService.showUpdateMessage();
                alert(sitemapMessage);
                
                // Try to regenerate sitemap in development
                sitemapService.regenerateInDev().catch(err => 
                    console.log('Sitemap will be regenerated on next deployment')
                );
            } else {
                alert(`Error: ${response.error}`);
            }
        } catch (error) {
            console.error('Error saving blog:', error);
            alert('Failed to save blog post. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleEdit = (blog) => {
        setEditingBlog(blog);
        setFormData({
            slug: blog.slug,
            title: blog.title,
            excerpt: blog.excerpt,
            category: blog.category,
            author: blog.author,
            date: blog.date,
            heroImage: blog.heroImage || '',
            tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : '',
            content: blog.content
        });
        if (blog.heroImage) {
            setImagePreview(blog.heroImage);
        }
        setShowForm(true);
    };

    const handleDelete = async (id, slug) => {
        if (window.confirm('Are you sure you want to delete this blog post?')) {
            try {
                const response = await blogService.deletePost(slug);
                
                if (response.success) {
                    await loadBlogs();
                    alert('Blog deleted successfully!');
                } else {
                    alert(`Error: ${response.error}`);
                }
            } catch (error) {
                console.error('Error deleting blog:', error);
                alert('Failed to delete blog post. Please try again.');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            slug: '',
            title: '',
            excerpt: '',
            category: 'SEO',
            author: 'Zia Abdullah',
            date: new Date().toISOString().split('T')[0],
            heroImage: '',
            tags: '',
            content: ''
        });
        setEditingBlog(null);
        setShowForm(false);
        setImagePreview(null);
        setImageFile(null);
    };


    return (
        <div className="admin-dashboard">
            <SEO 
                title="Dashboard Services | Digi Network" 
                description="Learn more about our Dashboard services to grow your business online with Digi Network." 
                canonical="/admin/dashboard" 
            />

            <div className="admin-header">
                <div className="container">
                    <div className="admin-header-content">
                        <div className="admin-header-left">
                            <h1>Blog Management Dashboard</h1>
                            <p className="admin-subtitle">Manage your blog posts and content</p>
                        </div>
                        <button onClick={handleLogout} className="btn-logout">
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            <div className="container admin-content">
                <div className="dashboard-actions">
                    {!showForm && (
                        <button onClick={() => setShowForm(true)} className="btn btn-primary btn-large">
                            + Create New Blog Post
                        </button>
                    )}
                    {showForm && (
                        <button onClick={resetForm} className="btn btn-secondary">
                            ← Back to List
                        </button>
                    )}
                </div>

                {showForm && (
                    <div className="blog-form-container">
                        <h2>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
                        <form onSubmit={handleSubmit} className="blog-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Title *</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter blog title"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug *</label>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="url-friendly-slug"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Excerpt *</label>
                                <textarea
                                    name="excerpt"
                                    value={formData.excerpt}
                                    onChange={handleInputChange}
                                    required
                                    rows="3"
                                    placeholder="Brief description of the blog post"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Category *</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="SEO">SEO</option>
                                        <option value="Paid Ads">Paid Ads</option>
                                        <option value="Web Development">Web Development</option>
                                        <option value="Email Marketing">Email Marketing</option>
                                        <option value="Digital Marketing">Digital Marketing</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Author</label>
                                    <input
                                        type="text"
                                        name="author"
                                        value={formData.author}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Publish Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Hero Image</label>
                                <div className="image-upload-container">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="image-upload-input"
                                        id="heroImageUpload"
                                    />
                                    <label htmlFor="heroImageUpload" className="image-upload-label">
                                        <span className="upload-icon">📷</span>
                                        <span>Choose Image</span>
                                    </label>
                                    {imagePreview && (
                                        <div className="image-preview">
                                            <img src={imagePreview} alt="Preview" />
                                            <button type="button" onClick={() => {
                                                setImagePreview(null);
                                                setFormData(prev => ({ ...prev, heroImage: '' }));
                                            }} className="remove-image">×</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Tags</label>
                                <input
                                    type="text"
                                    name="tags"
                                    value={formData.tags}
                                    onChange={handleInputChange}
                                    placeholder="SEO, Digital Marketing, Google Ads (comma separated)"
                                />
                                <small>Enter tags separated by commas</small>
                            </div>

                            <div className="form-group">
                                <label>Content *</label>
                                <ContentEditor
                                    value={formData.content}
                                    onChange={handleInputChange}
                                    name="content"
                                />
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary btn-large" disabled={uploading}>
                                    {uploading ? 'Uploading...' : (editingBlog ? 'Update Blog Post' : 'Publish Blog Post')}
                                </button>
                                <button type="button" onClick={resetForm} className="btn btn-secondary" disabled={uploading}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="blogs-list">
                    <div className="blogs-list-header">
                        <h2>All Blog Posts ({blogs.length})</h2>
                        <div className="blogs-stats">
                            <span className="stat-badge">Total Posts: {blogs.length}</span>
                        </div>
                    </div>
                    {blogs.length === 0 ? (
                        <div className="empty-state">
                            <p>No blog posts yet. Create your first one!</p>
                        </div>
                    ) : (
                        <div className="blogs-grid">
                            {blogs.map(blog => (
                                <div key={blog.id} className="blog-item">
                                    <div className="blog-item-header">
                                        <span className="blog-category">{blog.category}</span>
                                        <span className="blog-date">{blog.date}</span>
                                    </div>
                                    <h3>{blog.title}</h3>
                                    <p>{blog.excerpt}</p>
                                    <div className="blog-item-meta">
                                        <span>{blog.author}</span>
                                    </div>
                                    {blog.tags && blog.tags.length > 0 && (
                                        <div className="blog-item-tags">
                                            {blog.tags.slice(0, 3).map((tag, idx) => (
                                                <span key={idx} className="mini-tag">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="blog-item-actions">
                                        <button
                                            onClick={() => handleEdit(blog)}
                                            className="btn-edit"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(blog.id, blog.slug)}
                                            className="btn-delete"
                                        >
                                            Delete
                                        </button>
                                        <a href={`/blog/${blog.slug}`} target="_blank" rel="noopener noreferrer" className="btn-view">
                                            View Live
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
